import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { UserNotification } from '../../utils/userNotifications';
import { notificationPreviewPath, withNotificationEmbed } from '../../utils/userNotifications';
import './NotificationPreviewModal.css';

export type NotificationPreviewModalProps = {
  notification: UserNotification;
  viewerRole?: string;
  onClose: () => void;
  onOpenFullPage: () => void;
};

const NotificationPreviewModal: React.FC<NotificationPreviewModalProps> = ({
  notification,
  viewerRole,
  onClose,
  onOpenFullPage,
}) => {
  const path = notificationPreviewPath(notification, viewerRole);
  const src = path ? withNotificationEmbed(path) : null;
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="notif-preview-overlay" role="presentation" onClick={onClose}>
      <div
        className="notif-preview-popout"
        role="dialog"
        aria-labelledby="notif-preview-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="notif-preview-head">
          <div className="notif-preview-head-copy">
            <h2 id="notif-preview-title">{notification.title || 'Notification'}</h2>
            {notification.message ? <p>{notification.message}</p> : null}
          </div>
          <div className="notif-preview-head-actions">
            {src ? (
              <button type="button" className="notif-preview-open" onClick={onOpenFullPage}>
                Open page
              </button>
            ) : null}
            <button type="button" className="notif-preview-close" onClick={onClose} aria-label="Close preview">
              <X size={16} />
            </button>
          </div>
        </header>
        {src ? (
          <div className="notif-preview-frame-wrap">
            {!loaded ? <p className="notif-preview-loading">Loading preview…</p> : null}
            <iframe
              className="notif-preview-frame"
              title={notification.title || 'Notification preview'}
              src={src}
              onLoad={() => setLoaded(true)}
            />
          </div>
        ) : (
          <p className="notif-preview-empty">This notification does not open a page.</p>
        )}
      </div>
    </div>
  );
};

export default NotificationPreviewModal;
